import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import {
  getSpecificInstructionDetail,
  notifyAccept,
  markAccepted,
  getTechnicalSupportOfficers,
  notifyAuthorize,
  getOperationManagers,
  getShiftSupervisors,
  markAuthorized,
  notifyCarryOutInstruction,
  markCarriedOutInstruction,
  notifySubmitReport,
  getSystemEngineer,
  markSubmitted,
  downloadSpecificInstructionPdf,
  deleteSpecificInstruction,
} from '../../../services/specificInstructionService';
import { useProfile } from '../../../Contexts/ProfileContext';
import RejectButton from '../../../components/RejectButton/RejectButton';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import AddSpecificInstructionModal from './AddSpecificInstructionModal';

interface InstructionData {
  id: string;
  status?: string | null;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  title: string;
  site: string;
  unitStation: string;
  system: string;
  revision: string;
  hbs: string;
  serialNo?: string;
  formNo?: string;
  docType?: string;
  reasons: string[];
  description: string;
  created_by: string;
}

interface TSO {
  id: number;
  name: string;
}

const SpecificInstructionDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const profile = useProfile();

  const [data, setData] = useState<InstructionData | null>(null);
  const [rawInstructionData, setRawInstructionData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [tsoList, setTsoList] = useState<TSO[]>([]);
  const [selectedTso, setSelectedTso] = useState<string>('');
  const [notifyLoading, setNotifyLoading] = useState(false);

  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [notified, setNotified] = useState(false);
  const [acceptApproverIds, setAcceptApproverIds] = useState<string[]>([]);
  const [authorizeApproverIds, setAuthorizeApproverIds] = useState<string[]>([]);
  const [instructionApproverIds, setInstructionApproverIds] = useState<string[]>([]);
  const [submitReportApproverIds, setSubmitReportApproverIds] = useState<string[]>([]);

  // Notify Authorize
  const [showAuthorizeModal, setShowAuthorizeModal] = useState(false);
  const [operationManagers, setOperationManagers] = useState<any[]>([]);
  const [selectedOperationManager, setSelectedOperationManager] = useState('');
  const [authorizeLoading, setAuthorizeLoading] = useState(false);
  const [authorized, setAuthorized] = useState(false);

  const [showAuthorizeConfirm, setShowAuthorizeConfirm] = useState(false);
  const [markAuthorizeLoading, setMarkAuthorizeLoading] = useState(false);

  const [toastTitle, setToastTitle] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  const [showCarryOutModal, setShowCarryOutModal] = useState(false);
  const [carryOutLoading, setCarryOutLoading] = useState(false);

  const [showCarryOutConfirm, setShowCarryOutConfirm] = useState(false);
  const [carryOutInstructionLoading, setCarryOutInstructionLoading] = useState(false);

  const [carryOutNotified, setCarryOutNotified] = useState(false);

  const [shiftSupervisors, setShiftSupervisors] = useState<any[]>([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState('');

  const [showSubmitReportModal, setShowSubmitReportModal] = useState(false);
  const [submitReportLoading, setSubmitReportLoading] = useState(false);
  const [submitReportNotified, setSubmitReportNotified] = useState(false);
  const [showSubmitReportConfirm, setShowSubmitReportConfirm] = useState(false);

  const [markSubmittedLoading, setMarkSubmittedLoading] = useState(false);

  const [submitReportUsers, setSubmitReportUsers] = useState<any[]>([]);
  const [selectedSubmitSupervisor, setSelectedSubmitSupervisor] = useState('');

  const [currentUserId, setCurrentUserId] = useState('unknown_user');

  const [systemEngineerIds, setSystemEngineerIds] = useState<string[]>([]);

  // Edit / Delete (New status only)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    setCurrentUserId(localStorage.getItem('currentUserID') || '');
  }, []);

  const fetchInstruction = async () => {
    try {
      setLoading(true);
      const response = await getSpecificInstructionDetail(id as string);
      const apiData = response?.data || response;

      setRawInstructionData(apiData);

      const transformedData: InstructionData = {
        id: apiData.reference_number || id || '',
        status: apiData.status ?? 'New',

        startDate: apiData.start_date,
        startTime: apiData.start_time,
        endDate: apiData.end_date,
        endTime: apiData.end_time,

        title: apiData.title,
        site: apiData.site,
        unitStation: apiData.unit_station,
        system: apiData.system,
        revision: apiData.rev,
        hbs: apiData.hbs,
        serialNo: apiData.reference_number,
        formNo: apiData.id?.toString(),
        docType: 'Form',
        reasons: [apiData.reason_for_specific_instruction || 'No reason provided'],
        description: apiData.specific_instruction || apiData.description || '',
        created_by: apiData.created_by.id,
      };

      setData(transformedData);

      const getPendingApproverIds = (workflowStages: any[], stageName: string) =>
        (Array.isArray(workflowStages) ? workflowStages : [])
          .filter((stage: any) => stage?.stage === stageName)
          .flatMap((stage: any) =>
            (Array.isArray(stage?.approvers) ? stage.approvers : [])
              .filter((approver: any) => approver?.status === 'PENDING')
              .map((approver: any) => (approver?.user?.id ?? approver?.user_id ?? approver?.id)?.toString())
              .filter(Boolean),
          );

      setAcceptApproverIds(getPendingApproverIds(apiData.workflow_stages, 'ACCEPT'));

      setAuthorizeApproverIds(getPendingApproverIds(apiData.workflow_stages, 'AUTHORIZE'));

      setInstructionApproverIds(getPendingApproverIds(apiData.workflow_stages, 'INSTRUCTION'));

      setSubmitReportApproverIds(getPendingApproverIds(apiData.workflow_stages, 'SUBMIT'));

      setSystemEngineerIds(getPendingApproverIds(apiData.workflow_stages, 'SYSTEM_ENGINEER'));

      if (apiData.status === 'Accept Pending' || apiData.status === 'Accepted') {
        setNotified(true);
      }
      if (apiData.status === 'Authorization Pending') {
        setNotified(true);
      }

      if (apiData.status === 'Authorized') {
        setNotified(true);
        setAuthorized(true);
      }

      if (apiData.status === 'Instruction Pending') {
        setNotified(true);
        setAuthorized(true);
      }

      if (apiData.status === 'Carried Out Instruction') {
        setNotified(true);
        setAuthorized(true);
        setCarryOutNotified(true);
      }

      if (apiData.status === 'Submission Pending') {
        setNotified(true);
        setAuthorized(true);
        setCarryOutNotified(true);
        setSubmitReportNotified(true);
      }

      if (apiData.status === 'Submitted' || apiData.status === 'Report Submitted') {
        setNotified(true);
        setAuthorized(true);
        setCarryOutNotified(true);
        setSubmitReportNotified(true);
      }
    } catch (error) {
      console.error('Error fetching instruction:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchInstruction();
  }, [id]);

  const handleOpenNotifyModal = async () => {
    setShowNotifyModal(true);
    try {
      const res = await getTechnicalSupportOfficers();
      const users: TSO[] = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name || 'Unknown',
      }));
      setTsoList(users);
    } catch (err) {
      console.error('Failed to load TSO list:', err);
    }
  };

  const handleNotifyDone = async () => {
    if (!selectedTso || !id) {
      alert('Please select a Technical Support Officer');
      return;
    }

    try {
      setNotifyLoading(true);

      const payload = {
        user_ids: [Number(selectedTso)],
      };

      await notifyAccept(id, payload);

      // Success
      setNotified(true);
      setData((prev) => (prev ? { ...prev, status: 'Accept pending' } : prev));

      setShowNotifyModal(false);
      setSelectedTso('');

      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    } catch (error: any) {
      console.error('Notify API Error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      let errorMsg = 'Failed to send notification. Please try again.';

      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message?.includes('Table') || error.message?.includes('SQLSTATE')) {
        errorMsg = 'Server configuration error. Please contact administrator.';
      }

      alert(errorMsg);
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setAcceptLoading(true);
      await markAccepted(id as string);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Accepted',
            }
          : prev,
      );
      setShowAcceptConfirm(false);
    } catch (error: any) {
      console.error('Accept failed:', error);
      alert('Failed to accept the instruction. Please try again.');
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleOpenAuthorizeModal = async () => {
    setShowAuthorizeModal(true);

    try {
      const res = await getOperationManagers();

      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name || 'Unknown',
      }));

      setOperationManagers(users);
    } catch (error) {
      console.error('Failed loading Operation Managers', error);
    }
  };

  const handleOpenCarryOutModal = async () => {
    setShowCarryOutModal(true);

    try {
      const res = await getShiftSupervisors();

      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name || 'Unknown',
      }));

      setShiftSupervisors(users);
    } catch (error) {
      console.error('Failed loading Shift Supervisors', error);
    }
  };

  const handleOpenSubmitReportModal = async () => {
    setShowSubmitReportModal(true);

    try {
      const res = await getShiftSupervisors();

      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name || 'Unknown',
      }));

      setSubmitReportUsers(users);
    } catch (error) {
      console.error('Failed loading system engineers', error);
    }
  };

  const handleAuthorizeDone = async () => {
    if (!selectedOperationManager || !id) {
      alert('Please select Operation Manager');
      return;
    }

    try {
      setAuthorizeLoading(true);

      const payload = {
        user_ids: [Number(selectedOperationManager)],
        specific_instruction_id: id,
      };

      await notifyAuthorize(id, payload);

      setAuthorized(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Authorize pending',
            }
          : prev,
      );

      setShowAuthorizeModal(false);
      setSelectedOperationManager('');

      setToastTitle('Notification Sent Successfully');
      setToastMessage('Operation Manager has been notified.');

      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    } catch (error) {
      console.error(error);
      alert('Failed to notify operation manager');
    } finally {
      setAuthorizeLoading(false);
    }
  };

  const handleMarkAuthorized = async () => {
    try {
      setMarkAuthorizeLoading(true);

      await markAuthorized(id as string);

      setAuthorized(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Authorized',
            }
          : prev,
      );

      setShowAuthorizeConfirm(false);

      setToastTitle('Authorized Successfully');
      setToastMessage('Specific instruction has been authorized.');

      setShowToast(true);

      setTimeout(() => {
        setShowToast(false);
      }, 4000);
    } catch (error) {
      console.error(error);
      alert('Failed to authorize instruction');
    } finally {
      setMarkAuthorizeLoading(false);
    }
  };

  const handleCarryOutDone = async () => {
    if (!selectedSupervisor) {
      alert('Please select Shift Supervisor');
      return;
    }

    try {
      setCarryOutLoading(true);

      await notifyCarryOutInstruction(id as string, {
        user_ids: [Number(selectedSupervisor)],
      });

      setShowCarryOutModal(false);
      setSelectedSupervisor('');

      setCarryOutNotified(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Instruction Pending',
            }
          : prev,
      );

      setToastTitle('Notification Sent');
      setToastMessage('Notification successfully sent to the Shift Supervisor to carry out the instruction.');

      setShowToast(true);

      setTimeout(() => {
        setShowToast(false);
      }, 4000);
    } catch (error) {
      console.error(error);
      alert('Failed to send notification');
    } finally {
      setCarryOutLoading(false);
    }
  };

  const handleCarryOutInstruction = async () => {
    try {
      setCarryOutInstructionLoading(true);

      await markCarriedOutInstruction(id as string);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Carried Out Instruction',
            }
          : prev,
      );

      setShowCarryOutConfirm(false);

      setToastTitle('Instruction Carried Out');
      setToastMessage('Specific instruction has been carried out successfully.');

      setShowToast(true);

      setTimeout(() => {
        setShowToast(false);
      }, 4000);
    } catch (error) {
      console.error(error);
      alert('Failed to carry out instruction');
    } finally {
      setCarryOutInstructionLoading(false);
    }
  };

  const handleSubmitReportDone = async () => {
    if (!selectedSubmitSupervisor) {
      alert('Please select Shift Supervisor');
      return;
    }

    try {
      setSubmitReportLoading(true);

      await notifySubmitReport(id as string, {
        user_ids: [Number(selectedSubmitSupervisor)],
      });

      setShowSubmitReportModal(false);
      setSelectedSubmitSupervisor('');

      setSubmitReportNotified(true);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Report Submission Pending',
            }
          : prev,
      );

      setToastTitle('Notification Sent');
      setToastMessage('Shift Supervisor has been notified to submit the report.');

      setShowToast(true);

      setTimeout(() => {
        setShowToast(false);
      }, 4000);
    } catch (error) {
      console.error(error);
      alert('Failed to send notification');
    } finally {
      setSubmitReportLoading(false);
    }
  };

  const handleSubmitReport = async () => {
    try {
      setMarkSubmittedLoading(true);

      await markSubmitted(id as string);

      setData((prev) =>
        prev
          ? {
              ...prev,
              status: 'Submitted',
            }
          : prev,
      );

      setShowSubmitReportConfirm(false);

      setToastTitle('Report Submitted');
      setToastMessage('Specific instruction report submitted successfully.');

      setShowToast(true);

      setTimeout(() => {
        setShowToast(false);
      }, 4000);
    } catch (error) {
      console.error(error);
      alert('Failed to submit report');
    } finally {
      setMarkSubmittedLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadLoading(true);

      const pdfBlob = await downloadSpecificInstructionPdf(id as string);

      const url = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));

      const link = document.createElement('a');
      link.href = url;

      link.setAttribute('download', `${data?.serialNo || 'specific-instruction'}.pdf`);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alert('Failed to download PDF');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id || deleteLoading || !data) return;

    const canDelete = data.status?.trim().toLowerCase() === 'new' && String(data.created_by) === String(currentUserId);

    if (!canDelete) {
      alert('This Specific Instruction cannot be deleted at this stage.');
      return;
    }

    try {
      setDeleteLoading(true);

      await deleteSpecificInstruction(Number(id));

      navigate('/SpecificInstruction');
    } catch (err: any) {
      console.error('Failed to delete Specific Instruction:', err);

      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to delete Specific Instruction';

      alert(errorMessage);
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading instruction details...</div>;
  }

  if (!data) {
    return <div className="p-8 text-center text-red-500">No data found.</div>;
  }

  const getStatusDisplay = (status: string | null | undefined) => {
    if (status === 'Report Submitted') {
      return { label: 'Report Submitted', dotClass: 'bg-green-500', textClass: 'text-green-500' };
    }
    return { label: status || '-', dotClass: 'bg-purple-500', textClass: 'text-purple-600' };
  };
  const canEditOrDelete = data.status?.trim().toLowerCase() === 'new' && String(data.created_by) === String(currentUserId);

  const formatDateTimeRange = () => {
    if (!data?.startDate && !data?.endDate) {
      return '-';
    }

    const formatDate = (date?: string) => {
      if (!date) return '-';

      const parsedDate = new Date(date);

      if (Number.isNaN(parsedDate.getTime())) {
        return date;
      }

      return parsedDate.toLocaleDateString('en-GB');
    };

    const formatTime = (time?: string) => {
      if (!time) return '-';

      // If API returns HH:mm:ss or HH:mm
      const [hours, minutes] = time.split(':');

      if (hours === undefined || minutes === undefined) {
        return time;
      }

      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    };

    return `${formatDate(data.startDate)}, ${formatTime(data.startTime)} to ${formatDate(data.endDate)}, ${formatTime(data.endTime)}`;
  };

  const statusDisplay = getStatusDisplay(data.status);

  return (
    <div className="w-full bg-white pr-6 relative min-h-screen">
      {/* Action Button */}
      <div className="absolute right-6 top-6 z-10 flex items-center gap-3">
        {canEditOrDelete && (
          <div className="flex items-center gap-2">
            {/* Edit */}
            <button
              onClick={handleEditClick}
              className="p-2.5 rounded-lg border border-gray-300 text-blue-600 hover:bg-blue-50 transition-all"
              title="Edit"
            >
              <Pencil size={18} />
            </button>

            {/* Delete */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-2.5 rounded-lg border border-gray-300 text-red-600 hover:bg-red-50 transition-all"
              title="Delete"
            >
              <Trash2 size={18} />
            </button>
          </div>
        )}

        {/* =========================
      WORKFLOW ACTIONS
      ========================= */}

        {!notified && data.created_by == currentUserId ? (
          <button
            onClick={handleOpenNotifyModal}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all"
          >
            Notify to Accept
          </button>
        ) : data.status === 'Accept Pending' && acceptApproverIds.includes(currentUserId) ? (
          <div className="flex items-center gap-3">
            <RejectButton title="Specific Instruction" id={id!} apiPath="specific-instructions" onSuccess={fetchInstruction} />

            <button
              onClick={() => setShowAcceptConfirm(true)}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              Accept
            </button>
          </div>
        ) : data.status === 'Accepted' && !authorized && data.created_by == currentUserId ? (
          <button
            onClick={handleOpenAuthorizeModal}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all"
          >
            Notify to Authorize
          </button>
        ) : data.status === 'Authorization Pending' && authorizeApproverIds.includes(currentUserId) ? (
          <div className="flex items-center gap-3">
            <RejectButton title="Specific Instruction" id={id!} apiPath="specific-instructions" onSuccess={fetchInstruction} />

            <button
              onClick={() => setShowAuthorizeConfirm(true)}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              Authorize
            </button>
          </div>
        ) : data.status === 'Authorized' && !carryOutNotified && data.created_by == currentUserId ? (
          <button
            onClick={handleOpenCarryOutModal}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all"
          >
            Notify to Carry Out Instruction
          </button>
        ) : data.status === 'Instruction Pending' && instructionApproverIds.includes(currentUserId) ? (
          <div className="flex items-center gap-3">
            <RejectButton title="Specific Instruction" id={id!} apiPath="specific-instructions" onSuccess={fetchInstruction} />

            <button
              onClick={() => setShowCarryOutConfirm(true)}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              Carry Out Instruction
            </button>
          </div>
        ) : data.status === 'Carried Out Instruction' && data.created_by == currentUserId && !submitReportNotified ? (
          <button
            onClick={handleOpenSubmitReportModal}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all"
          >
            Notify to Submit Report
          </button>
        ) : data.status === 'Submission Pending' && submitReportApproverIds.includes(currentUserId) ? (
          <div className="flex items-center gap-3">
            <RejectButton title="Specific Instruction" id={id!} apiPath="specific-instructions" onSuccess={fetchInstruction} />

            <button
              onClick={() => setShowSubmitReportConfirm(true)}
              className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              Submit Report
            </button>
          </div>
        ) : (data.status === 'Submitted' || data.status === 'Report Submitted') && data.created_by == currentUserId ? (
          <button
            onClick={handleDownloadPdf}
            disabled={downloadLoading}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {downloadLoading && (
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />

                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}

            {downloadLoading ? 'Downloading...' : 'Download'}
          </button>
        ) : null}
      </div>

      {/* Header */}
      <div className="mb-6 pt-4">
        <div className="flex gap-4">
          <h2 className="text-2xl font-semibold text-gray-900">{data.id}</h2>
          <div className="flex items-center gap-1 mt-1">
            <span className={`w-2 h-2 ${statusDisplay.dotClass} rounded-full`}></span>
            <span className={`${statusDisplay.textClass} font-medium`}>{statusDisplay.label}</span>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">📅 {formatDateTimeRange()}</p>
      </div>

      <h3 className="text-sm text-gray-500 font-semibold mb-4">Title</h3>
      <p className="mb-6 text-black-700 font-semibold">{data.title}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-6 gap-x-12 mb-8">
        <div>
          <p className="text-gray-500 text-sm font-semibold mb-1">Site</p>
          <p className="text-sm font-semibold">{data.site}</p>
        </div>
        <div>
          <p className="text-gray-500 text-sm font-semibold mb-1">Unit-Station</p>
          <p className="text-sm font-semibold">{data.unitStation}</p>
        </div>
        <div>
          <p className="text-gray-500 text-sm font-semibold mb-1">System</p>
          <p className="text-sm font-semibold">{data.system}</p>
        </div>
        <div>
          <p className="text-gray-500 text-sm font-semibold mb-1">Rev</p>
          <p className="text-sm font-semibold">{data.revision}</p>
        </div>
        <div>
          <p className="text-gray-500 text-sm font-semibold mb-1">HBS</p>
          <p className="text-sm font-semibold">{data.hbs}</p>
        </div>
        <div>
          <p className="text-gray-500 text-sm font-semibold mb-1">Serial No.</p>
          <p className="text-sm font-semibold">{data.serialNo}</p>
        </div>
        <div>
          <p className="text-gray-500 text-sm font-semibold mb-1">Form No.</p>
          <p className="text-sm font-semibold">{data.formNo}</p>
        </div>
        <div>
          <p className="text-gray-500 text-sm font-semibold mb-1">Doc Type</p>
          <p className="text-sm font-semibold">{data.docType}</p>
        </div>
      </div>

      <div className="mb-6">
        <h4 className="font-semibold text-gray-500 mb-2">Reason for Specific Instruction</h4>

        <div className="space-y-2 text-gray-700 [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6">
          {data.reasons?.map((reason, index) => <div key={index} dangerouslySetInnerHTML={{ __html: reason }} />)}
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-500 mb-2">
          Specific Instructions (deatils to of observations/recordings required including intervals):
        </h4>

        <div
          className="text-gray-700 [&_ul]:list-disc [&_ul]:ml-6 [&_ol]:list-decimal [&_ol]:ml-6 "
          dangerouslySetInnerHTML={{
            __html: data.description || '',
          }}
        />
      </div>

      {/* Notify Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[440px] p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Notify to Accept</h3>
              <button
                onClick={() => {
                  setShowNotifyModal(false);
                  setSelectedTso('');
                }}
                className="text-2xl text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <label className="block text-sm text-gray-600 mb-2">Select Technical Support Officer</label>
            <select
              value={selectedTso}
              onChange={(e) => setSelectedTso(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">Select Technical Support Officer</option>
              {tsoList.map((tso) => (
                <option key={tso.id} value={tso.id}>
                  {tso.name}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowNotifyModal(false);
                  setSelectedTso('');
                }}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleNotifyDone}
                disabled={!selectedTso || notifyLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {notifyLoading ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Authorize Notify Modal */}
      {showAuthorizeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[440px] p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Notify to Authorize</h3>

              <button
                onClick={() => {
                  setShowAuthorizeModal(false);
                  setSelectedOperationManager('');
                }}
                className="text-2xl text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <label className="block text-sm text-gray-600 mb-2">Select Operation Manager</label>

            <select
              value={selectedOperationManager}
              onChange={(e) => setSelectedOperationManager(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="">Select Operation Manager...</option>

              {operationManagers.map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.name}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAuthorizeModal(false);
                  setSelectedOperationManager('');
                }}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleAuthorizeDone}
                disabled={!selectedOperationManager || authorizeLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {authorizeLoading ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {showToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-green-600 text-white rounded-xl px-5 py-4 shadow-xl flex gap-3 w-80">
          <span className="text-2xl">✅</span>
          <div>
            <p className="font-semibold">{toastTitle || 'Notification Sent Successfully'}</p>
            <p className="text-sm mt-1">{toastMessage || 'Technical Support Officer has been notified.'}</p>
          </div>
        </div>
      )}

      {/* Accept Confirmation Modal */}
      {showAcceptConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[380px] p-6 shadow-2xl text-center">
            <h3 className="text-lg font-semibold mb-6">Are you sure you want to accept?</h3>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowAcceptConfirm(false)}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAccept}
                disabled={acceptLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {acceptLoading ? 'Accepting...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Authorize Confirmation Modal */}
      {showAuthorizeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[380px] p-6 shadow-2xl text-center">
            <h3 className="text-lg font-semibold mb-6">Are you sure you want to Authorize?</h3>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowAuthorizeConfirm(false)}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAuthorized}
                disabled={markAuthorizeLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {markAuthorizeLoading ? 'Authorizing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCarryOutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[440px] p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Notify to Carry Out Instruction</h3>

              <button
                onClick={() => {
                  setShowCarryOutModal(false);
                  setSelectedSupervisor('');
                }}
                className="text-2xl text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <label className="block text-sm text-gray-600 mb-2">Select Shift Supervisor / Assistant</label>

            <select
              value={selectedSupervisor}
              onChange={(e) => setSelectedSupervisor(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3"
            >
              <option value="">Select Shift Supervisor / Assistant</option>

              {shiftSupervisors.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCarryOutModal(false);
                  setSelectedSupervisor('');
                }}
                className="px-6 py-2.5 border border-gray-300 rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleCarryOutDone}
                disabled={!selectedSupervisor || carryOutLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg"
              >
                {carryOutLoading ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCarryOutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[380px] p-6 shadow-2xl text-center">
            <h3 className="text-lg font-semibold mb-6">Are you sure you want to Carry Out this Instruction?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowCarryOutConfirm(false)}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleCarryOutInstruction}
                disabled={carryOutInstructionLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {carryOutInstructionLoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSubmitReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[440px] p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Notify to Submit Report</h3>

              <button
                onClick={() => {
                  setShowSubmitReportModal(false);
                  setSelectedSubmitSupervisor('');
                }}
                className="text-2xl text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <label className="block text-sm text-gray-600 mb-2">Select Shift Supervisor</label>

            <select
              value={selectedSubmitSupervisor}
              onChange={(e) => setSelectedSubmitSupervisor(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3"
            >
              <option value="">Select Shift Supervisor</option>

              {submitReportUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowSubmitReportModal(false);
                  setSelectedSubmitSupervisor('');
                }}
                className="px-6 py-2.5 border border-gray-300 rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleSubmitReportDone}
                disabled={!selectedSubmitSupervisor || submitReportLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg"
              >
                {submitReportLoading ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSubmitReportConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-[380px] p-6 shadow-2xl text-center">
            <h3 className="text-lg font-semibold mb-6">Are you sure you want to submit report?</h3>

            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowSubmitReportConfirm(false)}
                className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleSubmitReport}
                disabled={markSubmittedLoading}
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {markSubmittedLoading ? 'Submitting...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal (New status only) */}
      {showEditModal && (
        <AddSpecificInstructionModal
          editData={rawInstructionData}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchInstruction();
          }}
          onError={(message) => alert(message)}
        />
      )}

      {/* Delete Confirmation Modal (New status only) */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Are you sure you want to delete this Specific Instruction?"
        message=""
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onCancel={() => {
          if (!deleteLoading) setShowDeleteConfirm(false);
        }}
        onConfirm={handleDeleteConfirm}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default SpecificInstructionDetailsPage;
